import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Settings as SettingsIcon, Check, Plus } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useReadingBook } from '../../hooks/useReadingBooks'
import { useReadingProgress } from '../../hooks/useReadingProgress'
import { supabase } from '../../lib/supabaseClient'
import { getCachedText, setCachedText } from '../../lib/readingContentCache'
import { PdfReader } from './reading/PdfReader'
import { EpubReader } from './reading/EpubReader'

const FONT_SIZES = ['sm', 'base', 'lg', 'xl']
const FONT_SIZE_KEY = 'phoenix:reading-font-size'

// ReadingReaderPage — /reading/:bookId/read
//
// Paginated plain-text reader. The earlier CSS-multi-column approach
// left content clipped at the bottom of a single page because the
// article element stays constrained to its parent's width — columns
// don't actually spill horizontally without explicit width math, and
// the result was the "cuted page" the owner reported. Replaced with
// explicit JS pagination: split the body into page-sized character
// chunks (with breaks at paragraph/sentence boundaries when possible)
// and render each chunk as a flex child in a horizontal scroll-snap
// container. Snap points are now real DOM elements so swipes /
// scrollIntoView() / scroll-snap-align all behave as expected.
//
// Tap (or swipe) the start third of the screen to flip back, the end
// third to flip forward. Logical positioning flips Arabic books
// automatically. Position is stored as percent-through-pages so old
// progress rows from before this rewrite still resume roughly right.
export function ReadingReaderPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { bookId } = useParams()
  const { book, loading: bookLoading, addHighlight, updateBook } = useReadingBook(bookId)
  const { position: savedPosition, loaded: progressLoaded, saveProgress } = useReadingProgress(bookId)

  const [text, setText] = useState(null)
  const [loadingText, setLoadingText] = useState(true)
  const [error, setError] = useState(null)
  const [fontSize, setFontSize] = useState(() => {
    try { return localStorage.getItem(FONT_SIZE_KEY) || 'base' }
    catch { return 'base' }
  })
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [selection, setSelection] = useState('')
  const [savingHighlight, setSavingHighlight] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [viewport, setViewport] = useState({ w: 0, h: 0 })

  const scrollRef = useRef(null)
  const restoredRef = useRef(false)
  const saveTimerRef = useRef(null)

  const isRtl = book?.language === 'ar'

  // ---- Body text: cache first, network second ----
  useEffect(() => {
    if (!book || !book.text_path) return undefined
    let alive = true
    setLoadingText(true)
    setError(null)
    ;(async () => {
      const cached = await getCachedText(book.id)
      if (cached) {
        if (alive) {
          setText(cached)
          setLoadingText(false)
        }
        return
      }
      const { data: signed } = await supabase.storage
        .from('reading-content')
        .createSignedUrl(book.text_path, 3600)
      if (!signed?.signedUrl) {
        if (alive) {
          setError(t('reading.readerLoadFailed'))
          setLoadingText(false)
        }
        return
      }
      try {
        const res = await fetch(signed.signedUrl)
        if (!res.ok) throw new Error(`fetch ${res.status}`)
        const body = await res.text()
        if (alive) {
          setText(body)
          setLoadingText(false)
          setCachedText(book.id, body)
        }
      } catch (e) {
        if (alive) {
          setError((e instanceof Error ? e.message : null) || t('reading.readerLoadFailed'))
          setLoadingText(false)
        }
      }
    })()
    return () => { alive = false }
  }, [book, t])

  // ---- Viewport measurement: drives the pagination math ----
  useEffect(() => {
    const measure = () => {
      const el = scrollRef.current
      if (!el) return
      setViewport({ w: el.clientWidth, h: el.clientHeight })
    }
    measure()
    if (typeof ResizeObserver !== 'undefined' && scrollRef.current) {
      const ro = new ResizeObserver(measure)
      ro.observe(scrollRef.current)
      return () => ro.disconnect()
    }
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // ---- Pagination ----
  const pages = useMemo(() => {
    if (!text) return []
    if (!viewport.w || !viewport.h) return [text]
    return approximatePages(text, viewport.w, viewport.h, fontSizeToPx(fontSize))
  }, [text, viewport.w, viewport.h, fontSize])

  // ---- Initial scroll restore once both pages + saved progress are in ----
  useEffect(() => {
    if (restoredRef.current) return
    if (!progressLoaded || pages.length === 0) return
    const el = scrollRef.current
    if (!el || !el.clientWidth) return
    restoredRef.current = true
    const targetPage = Math.max(0, Math.min(pages.length - 1,
      Math.round(((savedPosition || 0) / 100) * Math.max(1, pages.length - 1))))
    setPageIndex(targetPage)
    requestAnimationFrame(() => {
      const node = scrollRef.current
      if (!node) return
      node.scrollTo({ left: targetPage * node.clientWidth, behavior: 'auto' })
    })
  }, [progressLoaded, pages.length, savedPosition])

  // ---- Re-anchor scroll to the same page% when re-pagination happens
  // (font change, viewport change). We don't re-fire on every pages
  // update — only when the count would have moved us off the page.
  const lastPagesCount = useRef(0)
  useEffect(() => {
    if (!restoredRef.current) return
    if (pages.length === 0) return
    if (lastPagesCount.current === pages.length) return
    const el = scrollRef.current
    if (!el || !el.clientWidth) {
      lastPagesCount.current = pages.length
      return
    }
    const prevCount = lastPagesCount.current
    lastPagesCount.current = pages.length
    if (prevCount === 0) return
    // Map old pageIndex onto the new page count by percent.
    const pct = prevCount > 1 ? pageIndex / (prevCount - 1) : 0
    const newPage = Math.max(0, Math.min(pages.length - 1,
      Math.round(pct * Math.max(1, pages.length - 1))))
    setPageIndex(newPage)
    requestAnimationFrame(() => {
      const node = scrollRef.current
      if (!node) return
      node.scrollTo({ left: newPage * node.clientWidth, behavior: 'auto' })
    })
  }, [pages.length, pageIndex])

  // ---- Scroll listener ----
  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || !el.clientWidth || pages.length === 0) return
    const idx = Math.max(0, Math.min(pages.length - 1,
      Math.round(Math.abs(el.scrollLeft) / el.clientWidth)))
    if (idx !== pageIndex) setPageIndex(idx)
    const pct = pages.length > 1 ? (idx / (pages.length - 1)) * 100 : 0
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveProgress(pct), 600)
  }, [pages.length, pageIndex, saveProgress])

  // ---- Selection-based highlight ----
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection?.()
      const selText = sel ? sel.toString().trim() : ''
      if (selText && scrollRef.current && sel.anchorNode && scrollRef.current.contains(sel.anchorNode)) {
        setSelection(selText.slice(0, 500))
      } else {
        setSelection('')
      }
    }
    document.addEventListener('selectionchange', onSel)
    return () => document.removeEventListener('selectionchange', onSel)
  }, [])

  const onAddHighlight = useCallback(async () => {
    if (!selection || savingHighlight) return
    setSavingHighlight(true)
    const result = await addHighlight(selection, null)
    setSavingHighlight(false)
    if (!result.error) {
      setSelection('')
      try { window.getSelection?.()?.removeAllRanges?.() } catch { /* ignore */ }
    }
  }, [selection, savingHighlight, addHighlight])

  // ---- Page-turn tap zones ----
  const turnPage = useCallback((direction) => {
    const sel = window.getSelection?.()
    if (sel && sel.toString().trim()) return
    const el = scrollRef.current
    if (!el || !el.clientWidth) return
    const step = direction === 'next' ? el.clientWidth : -el.clientWidth
    el.scrollBy({ left: step, behavior: 'smooth' })
  }, [])

  const onChangeFontSize = useCallback((next) => {
    setFontSize(next)
    try { localStorage.setItem(FONT_SIZE_KEY, next) } catch { /* ignore */ }
  }, [])

  const onMarkFinished = useCallback(async () => {
    setSettingsOpen(false)
    await updateBook({ status: 'finished' })
  }, [updateBook])

  // ---- Gates ----
  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || bookLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }
  if (!book) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <p dir="auto" className="mb-4 text-base opacity-80">{t('reading.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
        >
          {t('reading.back')}
        </button>
      </div>
    )
  }
  // Uploaded PDF → in-app pdf.js reader (bytes from on-device IndexedDB).
  if (book.file_format === 'pdf') {
    return (
      <PdfReader
        book={book}
        savedPosition={savedPosition}
        progressLoaded={progressLoaded}
        saveProgress={saveProgress}
        onMarkFinished={onMarkFinished}
        t={t}
      />
    )
  }
  // Uploaded EPUB → in-app epubjs reader.
  if (book.file_format === 'epub') {
    return (
      <EpubReader
        book={book}
        savedPosition={savedPosition}
        progressLoaded={progressLoaded}
        saveProgress={saveProgress}
        onMarkFinished={onMarkFinished}
        t={t}
      />
    )
  }
  if (!book.text_path) {
    return <Navigate to={`/reading/${book.id}`} replace />
  }

  const fontClass =
    fontSize === 'sm' ? 'text-sm leading-7'
    : fontSize === 'lg' ? 'text-lg leading-9'
    : fontSize === 'xl' ? 'text-xl leading-10'
    : 'text-base leading-8'

  return (
    <div className="mx-auto flex h-[100dvh] max-w-2xl flex-col">
      <header className="flex items-center justify-between border-b border-neutral-200 bg-card px-4 py-2 dark:border-neutral-800">
        <button
          type="button"
          onClick={() => navigate(`/reading/${book.id}`)}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        <div className="min-w-0 flex-1 px-2 text-center">
          <p dir="auto" className="line-clamp-1 text-sm font-medium">{book.title}</p>
          {book.author && (
            <p dir="auto" className="line-clamp-1 text-[10px] opacity-60">{book.author}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.readerSettings')}
        >
          <SettingsIcon className="h-4 w-4" />
        </button>
      </header>

      {settingsOpen && (
        <div className="border-b border-neutral-200 bg-card px-4 py-3 dark:border-neutral-800">
          <div className="flex items-center justify-between gap-3">
            <span dir="auto" className="text-xs font-medium opacity-70">{t('reading.fontSize')}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  const idx = Math.max(0, FONT_SIZES.indexOf(fontSize) - 1)
                  onChangeFontSize(FONT_SIZES[idx])
                }}
                className="grid h-8 w-8 place-items-center rounded-full bg-neutral-100 text-xs font-semibold dark:bg-neutral-800"
                aria-label={t('reading.fontDecrease')}
              >
                A−
              </button>
              <button
                type="button"
                onClick={() => {
                  const idx = Math.min(FONT_SIZES.length - 1, FONT_SIZES.indexOf(fontSize) + 1)
                  onChangeFontSize(FONT_SIZES[idx])
                }}
                className="grid h-8 w-8 place-items-center rounded-full bg-neutral-100 text-base font-semibold dark:bg-neutral-800"
                aria-label={t('reading.fontIncrease')}
              >
                A+
              </button>
            </div>
          </div>
          {book.status !== 'finished' && (
            <button
              type="button"
              onClick={onMarkFinished}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
            >
              <Check className="h-4 w-4" />
              <span dir="auto">{t('reading.markFinished')}</span>
            </button>
          )}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          dir={isRtl ? 'rtl' : 'ltr'}
          className="h-full overflow-x-auto overflow-y-hidden"
          style={{
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {loadingText ? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin opacity-60" />
            </div>
          ) : error ? (
            <p dir="auto" className="px-6 pt-10 text-center text-sm text-rose-500">{error}</p>
          ) : pages.length === 0 ? (
            <p dir="auto" className="px-6 pt-10 text-center text-sm opacity-70">{t('reading.readerEmpty')}</p>
          ) : (
            <div className="flex h-full" style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
              {pages.map((pageText, i) => (
                <Page
                  key={i}
                  pageText={pageText}
                  fontClass={fontClass}
                  isLastPage={i === pages.length - 1}
                  bookFinished={book.status === 'finished'}
                  onMarkFinished={onMarkFinished}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>

        {pages.length > 0 && !loadingText && !error && (
          <>
            <button
              type="button"
              onClick={() => turnPage('prev')}
              aria-label={t('reading.previousPage')}
              className="absolute inset-y-0 start-0 w-[28%] cursor-pointer"
              style={{ background: 'transparent' }}
            />
            <button
              type="button"
              onClick={() => turnPage('next')}
              aria-label={t('reading.nextPage')}
              className="absolute inset-y-0 end-0 w-[28%] cursor-pointer"
              style={{ background: 'transparent' }}
            />
            {pages.length > 1 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
                <span dir="ltr" className="rounded-full bg-black/45 px-2.5 py-0.5 text-[10px] font-medium text-white">
                  {pageIndex + 1} / {pages.length}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {selection && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
          <button
            type="button"
            onClick={onAddHighlight}
            disabled={savingHighlight}
            className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-rose-500 px-4 py-2 text-sm font-medium text-white shadow-lg disabled:opacity-60"
          >
            {savingHighlight ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span dir="auto">{t('reading.addSelectionHighlight')}</span>
          </button>
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------
// Page — one flex child, sized to the full viewport width via
// flex-shrink-0 + min-w-full. snap-start makes the swipe lock to
// each page boundary.
// ----------------------------------------------------------------
function Page({ pageText, fontClass, isLastPage, bookFinished, onMarkFinished, t }) {
  // Normalise per-paragraph whitespace at render time so soft-wrapped
  // source text doesn't preserve awkward line breaks across screen
  // sizes. Paragraph BLOCKS (separated by double-newlines) remain
  // distinct; the browser rewraps within each.
  const paragraphs = pageText
    .split(/\n{2,}/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter((p) => p.length > 0)

  return (
    <div
      className="h-full flex-shrink-0 overflow-hidden px-6 py-5"
      style={{ width: '100%', minWidth: '100%', scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
    >
      <div className={fontClass}>
        {paragraphs.map((para, j) => (
          <p key={j} dir="auto" className="mb-3">{para}</p>
        ))}
        {isLastPage && !bookFinished && (
          <div className="pt-4 text-center">
            <button
              type="button"
              onClick={onMarkFinished}
              className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
            >
              <Check className="h-4 w-4" />
              <span dir="auto">{t('reading.markFinished')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ----------------------------------------------------------------
// approximatePages — split the body text into roughly viewport-fit
// chunks at paragraph (then sentence) boundaries. The result is a
// few pages too short rather than too long so nothing gets clipped
// at the bottom — a small gap of whitespace at the end of a page
// is far less jarring than half a sentence disappearing.
// ----------------------------------------------------------------
function approximatePages(text, vw, vh, fontPx) {
  if (!text) return []
  const lineHeight = fontPx * 1.7
  const linesPerPage = Math.max(8, Math.floor((vh - 60) / lineHeight))
  // Arabic letterforms are narrower than Latin on average, but font
  // metrics vary; this average sits comfortably in both languages.
  const avgCharWidth = fontPx * 0.55
  const charsPerLine = Math.max(20, Math.floor((vw - 48) / avgCharWidth))
  // 0.78 safety margin so we under-fill rather than overflow.
  const charsPerPage = Math.max(400, Math.floor(charsPerLine * linesPerPage * 0.78))

  const sentenceTerminators = ['. ', '? ', '! ', '۔ ', '؟ ', '! ', '،\n', '.\n', '?\n', '!\n']

  const pages = []
  let cursor = 0
  while (cursor < text.length) {
    let end = Math.min(cursor + charsPerPage, text.length)
    if (end < text.length) {
      // Prefer paragraph break.
      const paraBreak = text.lastIndexOf('\n\n', end)
      if (paraBreak > cursor + Math.floor(charsPerPage * 0.5)) {
        end = paraBreak
      } else {
        // Fall back to nearest sentence terminator.
        let best = -1
        for (const sep of sentenceTerminators) {
          const idx = text.lastIndexOf(sep, end)
          if (idx > best) best = idx + sep.length
        }
        if (best > cursor + Math.floor(charsPerPage * 0.5)) {
          end = best
        }
      }
    }
    const page = text.slice(cursor, end).trim()
    if (page) pages.push(page)
    cursor = end
  }
  return pages.length ? pages : [text]
}

function fontSizeToPx(size) {
  switch (size) {
    case 'sm': return 14
    case 'lg': return 18
    case 'xl': return 20
    default: return 16
  }
}
